/**
 * Transaction API Client
 * Handles all transaction-related API requests using Axios with auth interceptors.
 */

import type {
  PaginatedResponse,
  TransactionWithCategory,
  ExpensesByCategoryResponse,
  RecentTransactionsResponse,
  CreateTransactionInput,
  TransactionFilters,
  Transaction,
  UserPreferencesResponse,
  UpdatePreferencesInput,
  YearlyReportResponse,
} from '../types';
import { api as axiosApi } from './axiosInstance';

export const transactionApi = {
  /**
   * Get transactions with filters and pagination
   */
  getTransactions: async (
    filters: TransactionFilters = {}
  ): Promise<PaginatedResponse<TransactionWithCategory>> => {
    const params = new URLSearchParams();

    if (filters.type) params.set('type', filters.type);
    if (filters.categoryId) params.set('categoryId', filters.categoryId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.offset) params.set('offset', filters.offset.toString());

    const queryString = params.toString();
    const endpoint = `/transactions${queryString ? `?${queryString}` : ''}`;

    return axiosApi.get<PaginatedResponse<TransactionWithCategory>>(endpoint);
  },

  /**
   * Get recent transactions
   */
  getRecentTransactions: async (
    limit: number = 10
  ): Promise<RecentTransactionsResponse> => {
    return axiosApi.get<RecentTransactionsResponse>(
      `/transactions/recent?limit=${limit}`
    );
  },

  /**
   * Get expenses grouped by category
   */
  getExpensesByCategory: async (
    startDate?: string,
    endDate?: string
  ): Promise<ExpensesByCategoryResponse> => {
    const params = new URLSearchParams();

    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);

    const queryString = params.toString();
    const endpoint = `/transactions/expenses-by-category${queryString ? `?${queryString}` : ''}`;

    return axiosApi.get<ExpensesByCategoryResponse>(endpoint);
  },

  /**
   * Create a new transaction
   */
  createTransaction: async (
    input: CreateTransactionInput
  ): Promise<{ success: true; data: Transaction }> => {
    return axiosApi.post<{ success: true; data: Transaction }>(
      '/transactions',
      input
    );
  },

  /**
   * Update an existing transaction
   */
  updateTransaction: async (
    transactionId: string,
    data: {
      amount?: number;
      description?: string;
      categoryId?: string;
      date?: string;
    }
  ): Promise<{ success: true; data: Transaction }> => {
    return axiosApi.put<{ success: true; data: Transaction }>(
      `/transactions/${transactionId}`,
      data
    );
  },

  /**
   * Delete a transaction
   */
  deleteTransaction: async (
    transactionId: string
  ): Promise<{ success: true }> => {
    return axiosApi.delete<{ success: true }>(
      `/transactions/${transactionId}`
    );
  },
};

export const authApi = {
  /**
   * Authenticate with email and password
   * Token is stored in HttpOnly cookie by backend (XSS protection)
   */
  login: async (email: string, password: string) => {
    const response = await axiosApi.post<{
      success: true;
      data: {
        user: { id: string; email: string; name: string; avatarUrl: string | null };
        expiresIn: number;
      };
    }>('/auth/login', { email, password });

    // Note: Token is automatically stored in HttpOnly cookie by backend
    // No localStorage storage needed (more secure)

    return response;
  },

  /**
   * Authenticate with Google OAuth
   * Token is stored in HttpOnly cookie by backend (XSS protection)
   */
  googleAuth: async (token: string) => {
    const response = await axiosApi.post<{
      success: true;
      data: {
        user: { id: string; email: string; name: string; avatarUrl: string | null };
        expiresIn: number;
      };
    }>('/auth/google', { token });

    // Note: Token is automatically stored in HttpOnly cookie by backend
    // No localStorage storage needed (more secure)

    return response;
  },

  /**
   * Get current user
   */
  getCurrentUser: async () => {
    return axiosApi.get<{
      success: true;
      data: { id: string; email: string; name: string; avatarUrl: string | null };
    }>('/auth/me');
  },

  /**
   * Change user password
   */
  changePassword: async (currentPassword: string, newPassword: string) => {
    return axiosApi.post<{ success: true }>('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  },

  /**
   * Logout - clears HttpOnly cookie via backend API
   */
  logout: async () => {
    try {
      await axiosApi.post<{ success: true }>('/auth/logout', {});
    } catch {
      // Ignore errors - cookie might already be cleared
    }
  },
};

export const userApi = {
  /**
   * Get user preferences (theme, budget config)
   */
  getPreferences: async (): Promise<UserPreferencesResponse> => {
    return axiosApi.get<UserPreferencesResponse>('/user/preferences');
  },

  /**
   * Update user preferences
   */
  updatePreferences: async (
    input: UpdatePreferencesInput
  ): Promise<UserPreferencesResponse> => {
    return axiosApi.patch<UserPreferencesResponse>('/user/preferences', input);
  },
};

export const reportApi = {
  /**
   * Get yearly report with monthly breakdown
   */
  getYearlyReport: async (year: number): Promise<YearlyReportResponse> => {
    return axiosApi.get<YearlyReportResponse>(`/reports/yearly?year=${year}`);
  },

  /**
   * Export yearly report as CSV
   * Note: Uses axiosInstance directly for blob response type
   */
  exportCsv: async (year: number): Promise<Blob> => {
    const { axiosInstance } = await import('./axiosInstance');
    const response = await axiosInstance.get(`/reports/export?year=${year}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Exchange Rates API Response Interface
export interface ExchangeRatesApiResponse {
  success: boolean;
  data: {
    rates: Record<string, number>;
    updatedAt: string | null;
    supportedCurrencies: string[];
  };
}

export const exchangeRatesApi = {
  /**
   * Get all exchange rates from the database.
   * Rates are refreshed every 12 hours from the external API.
   */
  getAll: async (): Promise<ExchangeRatesApiResponse> => {
    return axiosApi.get<ExchangeRatesApiResponse>('/exchange-rates');
  },
};

export interface MonthlyRevenueRecord {
  id: string;
  amount: number;
  year: number;
  month: number;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export interface MonthlyRevenueResponse {
  success: true;
  data: {
    current: MonthlyRevenueRecord | null;
    rollingAverage: number;
    lastSixMonths: MonthlyRevenueRecord[];
  };
}

export interface SaveMonthlyRevenueResponse {
  success: true;
  data: MonthlyRevenueRecord;
}

export const revenueApi = {
  /**
   * Get monthly revenue and rolling average
   */
  getMonthlyRevenue: async (year: number, month: number): Promise<MonthlyRevenueResponse> => {
    return axiosApi.get<MonthlyRevenueResponse>(`/revenue/monthly?year=${year}&month=${month}`);
  },

  /**
   * Save monthly revenue for a specific month
   */
  saveMonthlyRevenue: async (
    year: number,
    month: number,
    amount: number
  ): Promise<SaveMonthlyRevenueResponse> => {
    return axiosApi.post<SaveMonthlyRevenueResponse>('/revenue/monthly', {
      year,
      month,
      amount,
    });
  },
};

export interface RevenueEntryRecord {
  id: string;
  amount: number; // Amount in BRL
  description: string;
  date: string;
  currency: string; // Original input currency
  currencySnapshots: Record<string, number>; // Exchange rates at entry time
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export interface RevenueEntriesResponse {
  success: true;
  data: {
    entries: RevenueEntryRecord[];
    total: number;
    year: number;
    month: number;
  };
}

export interface CreateRevenueEntryResponse {
  success: true;
  data: RevenueEntryRecord;
}

export interface RevenueSummaryData {
  currentMonthTotal: number;
  currentYear: number;
  currentMonth: number;
  rollingAverage: number;
  monthsWithData: number;
  monthlyBreakdown: { year: number; month: number; total: number }[];
}

export interface RevenueSummaryResponse {
  success: true;
  data: RevenueSummaryData;
}

export interface UpdateRevenueEntryResponse {
  success: true;
  data: RevenueEntryRecord;
}

export interface DeleteRevenueEntryResponse {
  success: true;
}

export const revenueEntriesApi = {
  /**
   * Get revenue entries for a specific month
   */
  getEntries: async (year: number, month: number): Promise<RevenueEntriesResponse> => {
    return axiosApi.get<RevenueEntriesResponse>(`/revenue/entries?year=${year}&month=${month}`);
  },

  /**
   * Create a new revenue entry
   */
  createEntry: async (
    amount: number,
    description: string,
    date: string,
    currency?: string
  ): Promise<CreateRevenueEntryResponse> => {
    return axiosApi.post<CreateRevenueEntryResponse>('/revenue/entries', {
      amount,
      description,
      date,
      currency,
    });
  },

  /**
   * Update a revenue entry
   */
  updateEntry: async (
    entryId: string,
    data: { amount?: number; description?: string; date?: string }
  ): Promise<UpdateRevenueEntryResponse> => {
    return axiosApi.put<UpdateRevenueEntryResponse>(`/revenue/entries/${entryId}`, data);
  },

  /**
   * Delete a revenue entry
   */
  deleteEntry: async (entryId: string): Promise<DeleteRevenueEntryResponse> => {
    return axiosApi.delete<DeleteRevenueEntryResponse>(`/revenue/entries/${entryId}`);
  },

  /**
   * Get revenue summary (current month total and 6-month average)
   */
  getSummary: async (): Promise<RevenueSummaryResponse> => {
    return axiosApi.get<RevenueSummaryResponse>('/revenue/summary');
  },
};
